import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
    Mail, Phone, MapPin, GraduationCap, 
    Briefcase, ArrowLeft, Quote, 
    ChevronRight, Home, Globe
} from 'lucide-react';
import { motion } from 'framer-motion';
import { client, urlFor } from '../lib/sanity';

const TeacherDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [teacher, setTeacher] = useState(null);
    const [loading, setLoading] = useState(true);

    // Helper to generate id from name
    const generateId = (name) => {
        if (!name) return '';
        const cleanName = name.replace(/^(?:(?:Dr|Prof|Sri|Smt|Mr|Mrs|Ms)\.?\s*)+/i, '');
        return cleanName.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        
        setLoading(true);
        // Fetch all teachers to handle both direct ID and name-based generated ID
        const query = `*[_type == "teacher"]{
            ...,
            "deptLabel": department->name,
            "color": department->color
        }`;
        
        client.fetch(query)
            .then(data => {
                if (data && data.length > 0) {
                    const found = data.find(t => 
                        t._id === id || 
                        t._id === 'teacher-' + id || 
                        generateId(t.name) === id
                    );
                    setTeacher(found || null);
                }
                setLoading(false);
            })
            .catch(err => {
                console.error("Sanity fetch error:", err);
                setLoading(false);
            });
    }, [id]);

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-4">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                <p className="text-slate-500 mt-4 font-semibold">Loading Profile...</p>
            </div>
        );
    }

    if (!teacher) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-4">
                <h2 className="text-2xl font-display font-black text-primary mb-4">Teacher Not Found</h2>
                <button onClick={() => navigate(-1)} className="px-6 py-2 bg-primary text-white rounded-full font-bold">Go Back</button>
            </div>
        );
    }

    const accent = teacher.color || '#0C2B4E';

    return (
        <div className="min-h-screen bg-slate-50 pt-20 sm:pt-28 pb-12 sm:pb-20 px-4 sm:px-12">
            <div className="max-w-6xl mx-auto space-y-12 sm:space-y-20">
                
                {/* Top Section: Text and Blob Image */}
                <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-center">
                    
                    {/* Left Column: Info & About */}
                    <div className="lg:col-span-7 space-y-6 sm:space-y-8">
                        <div className="space-y-2">
                            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-display font-black text-primary leading-tight uppercase tracking-tighter">
                                {teacher.name}
                            </h1>
                            <p className="text-base sm:text-xl font-bold uppercase tracking-widest" style={{ color: accent }}>
                                {teacher.designation}
                            </p>
                        </div>
                        
                        <div className="space-y-3 sm:space-y-4 text-sm sm:text-lg text-slate-600 leading-relaxed font-medium text-justify">
                            {(teacher.about || []).map((p, i) => <p key={i}>{p}</p>)}
                        </div>

                        {/* Other Details - integrated gracefully as requested */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 pt-6 sm:pt-8 border-t border-slate-200 mt-6 sm:mt-8">
                            <DetailItem icon={<GraduationCap />} label="Qualification" value={teacher.qualification} color={accent} />
                            <DetailItem icon={<Briefcase />} label="Experience" value={teacher.experience} color={accent} />
                            <DetailItem icon={<MapPin />} label="Office Location" value={teacher.staffRoom} color={accent} />
                            <DetailItem icon={<Globe />} label="Department" value={teacher.deptLabel} color={accent} />
                            <DetailItem 
                                icon={<Mail />} 
                                label="Email Address" 
                                value={teacher.email} 
                                color={accent} 
                                isLink={`mailto:${teacher.email}`}
                            />
                            <DetailItem 
                                icon={<Phone />} 
                                label="Phone Number" 
                                value={teacher.phone} 
                                color={accent} 
                            />
                        </div>
                    </div>

                    {/* Right Column: Blob Image */}
                    <div className="lg:col-span-5 flex justify-center lg:justify-end">
                        <div 
                            className="w-[240px] h-[280px] xs:w-[300px] h-[350px] sm:w-[400px] sm:h-[450px] overflow-hidden shadow-2xl relative"
                            style={{ 
                                borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
                                border: `8px solid ${accent}20`
                            }}
                        >
                            <motion.img 
                                initial={{ scale: 1.1, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.8 }}
                                src={teacher.photo ? (teacher.photo.asset ? urlFor(teacher.photo).width(800).height(1000).fit('crop').url() : teacher.photo) : `https://ui-avatars.com/api/?name=${encodeURIComponent(teacher.name)}&size=800&background=0C2B4E&color=ffffff&bold=true`} 
                                alt={teacher.name}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>

                {/* Specialization Section */}
                {(teacher.specialization) && (
                    <motion.div 
                        initial={{ y: 20, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        viewport={{ once: true }}
                        className="space-y-6 pt-4"
                    >
                        <h2 className="text-xl font-display font-black text-primary uppercase tracking-tight">Specialization</h2>
                        <div className="flex flex-wrap gap-2 sm:gap-3">
                            {teacher.specialization.split(',').map((s, i) => (
                                <span 
                                    key={i}
                                    className="px-4 py-2 sm:px-6 sm:py-2.5 rounded-full bg-white border-2 border-slate-100 text-slate-700 font-bold text-xs sm:text-sm shadow-sm hover:border-accent hover:text-accent transition-all cursor-default"
                                >
                                    {s.trim()}
                                </span>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Publications Section */}
                {teacher.publications && teacher.publications.length > 0 && (
                    <motion.div 
                        initial={{ y: 20, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        viewport={{ once: true }}
                        className="space-y-6 pt-4"
                    >
                        <h2 className="text-xl font-display font-black text-primary uppercase tracking-tight">Publications</h2>
                        <div className="space-y-4 max-w-4xl">
                            {teacher.publications.map((pub, i) => (
                                <div 
                                    key={i} 
                                    className="p-4 sm:p-5 rounded-2xl bg-white border-2 border-slate-100 shadow-sm flex items-center justify-between group hover:border-accent/30 transition-colors cursor-pointer"
                                >
                                    <p className="text-sm sm:text-lg text-slate-700 font-medium text-justify">
                                        {pub}
                                    </p>
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center bg-slate-50 text-slate-400 group-hover:bg-accent group-hover:text-white transition-colors shrink-0 ml-3 sm:ml-4">
                                        <ChevronRight size={16} className="sm:w-5 sm:h-5" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Awards & Honours Section */}
                {teacher.awards_and_honours && teacher.awards_and_honours.length > 0 && (
                    <motion.div 
                        initial={{ y: 20, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        viewport={{ once: true }}
                        className="space-y-6 pt-4"
                    >
                        <h2 className="text-xl font-display font-black text-primary uppercase tracking-tight">Awards & Honours</h2>
                        <div className="space-y-4 max-w-4xl">
                            {teacher.awards_and_honours.map((award, i) => (
                                <div 
                                    key={i} 
                                    className="p-4 sm:p-5 rounded-2xl bg-white border-2 border-slate-100 shadow-sm flex items-center justify-between group hover:border-accent/30 transition-colors cursor-pointer"
                                >
                                    <p className="text-sm sm:text-lg text-slate-700 font-medium text-justify">
                                        {award}
                                    </p>
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center bg-slate-50 text-slate-400 group-hover:bg-accent group-hover:text-white transition-colors shrink-0 ml-3 sm:ml-4">
                                        <ChevronRight size={16} className="sm:w-5 sm:h-5" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Positions Handled Section */}
                {teacher.positions_handled && teacher.positions_handled.length > 0 && (
                    <motion.div 
                        initial={{ y: 20, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        viewport={{ once: true }}
                        className="space-y-6 pt-4"
                    >
                        <h2 className="text-xl font-display font-black text-primary uppercase tracking-tight">Positions Handled</h2>
                        <div className="space-y-4 max-w-4xl">
                            {teacher.positions_handled.map((position, i) => (
                                <div 
                                    key={i} 
                                    className="p-4 sm:p-5 rounded-2xl bg-white border-2 border-slate-100 shadow-sm flex items-center justify-between group hover:border-accent/30 transition-colors cursor-pointer"
                                >
                                    <p className="text-sm sm:text-lg text-slate-700 font-medium text-justify">
                                        {position}
                                    </p>
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center bg-slate-50 text-slate-400 group-hover:bg-accent group-hover:text-white transition-colors shrink-0 ml-3 sm:ml-4">
                                        <ChevronRight size={16} className="sm:w-5 sm:h-5" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Courses Handled Section */}
                {teacher.courses_handled && teacher.courses_handled.length > 0 && (
                    <motion.div 
                        initial={{ y: 20, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        viewport={{ once: true }}
                        className="space-y-6 pt-4"
                    >
                        <h2 className="text-xl font-display font-black text-primary uppercase tracking-tight">Courses Handled</h2>
                        <div className="space-y-4 max-w-4xl">
                            {teacher.courses_handled.map((course, i) => (
                                <div 
                                    key={i} 
                                    className="p-4 sm:p-5 rounded-2xl bg-white border-2 border-slate-100 shadow-sm flex items-center justify-between group hover:border-accent/30 transition-colors cursor-pointer"
                                >
                                    <p className="text-sm sm:text-lg text-slate-700 font-medium text-justify">
                                        {course}
                                    </p>
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center bg-slate-50 text-slate-400 group-hover:bg-accent group-hover:text-white transition-colors shrink-0 ml-3 sm:ml-4">
                                        <ChevronRight size={16} className="sm:w-5 sm:h-5" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Word from Teacher Section */}
                {teacher.wordFromTeacher && (
                    <motion.div 
                        initial={{ y: 20, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        viewport={{ once: true }}
                        className="space-y-6 pt-4"
                    >
                        <h2 className="text-xl font-display font-black text-primary uppercase tracking-tight">Word from teacher</h2>
                        <div className="relative p-6 sm:p-14 rounded-2xl sm:rounded-[2rem] bg-white border-2 border-slate-100 shadow-sm overflow-hidden group hover:border-accent/20 transition-all duration-500 max-w-5xl">
                            <div className="absolute top-4 left-4 sm:top-8 sm:left-8 text-slate-100 transition-colors duration-500 group-hover:text-accent/10">
                                <Quote size={40} className="sm:w-16 sm:h-16" fill="currentColor" />
                            </div>
                            <div className="relative z-10">
                                <p className="text-base sm:text-2xl lg:text-3xl font-display font-medium text-slate-800 leading-snug italic ml-4 sm:ml-12 text-justify">
                                    "{teacher.wordFromTeacher}"
                                </p>
                            </div>
                        </div>
                    </motion.div>
                )}

            </div>
        </div>
    );
};

const DetailItem = ({ icon, label, value, color, isLink }) => (
    <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}10`, color: color }}>
            {React.cloneElement(icon, { size: 18 })}
        </div>
        <div>
            <p className="text-[0.62rem] font-black text-slate-400 uppercase tracking-widest mb-1">{label}</p>
            {isLink ? (
                <a href={isLink} className="text-sm font-bold text-primary hover:text-accent transition-colors leading-tight block truncate max-w-[200px]">{value}</a>
            ) : (
                <p className="text-sm font-bold text-primary leading-tight">{value}</p>
            )}
        </div>
    </div>
);

export default TeacherDetail;
